'use client';

import { c as _c } from "react/compiler-runtime";
import type { BigIntish, ChainId, MarketId } from '@morpho-org/blue-sdk';
import type { InterfaceInputToken } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { cn } from '@morpho-org/ui-v6/utils';
import { Icon } from '@repo/ui';
import { Timelock } from '@repo/ui/icons';
import { formatPendingActionsLabel, VaultPendingTimelockInfo } from '@/components/VaultPage/components/VaultPendingTimelockInfo';
import { VaultPendingTimelockChangeCap, VaultPendingTimelockCountdown, VaultPendingTimelockRemoveMarket } from '@/components/VaultPage/VaultV1/VaultPendingTimelock/components';
import { getTimelockFunctionConfig } from '@/components/VaultPage/VaultV1/VaultPendingTimelock/utils';
import { StyledVaultPendingTimelock } from '@/components/VaultPage/VaultV1/VaultPendingTimelock/VaultPendingTimelock.components';
import type { PendingTimelockItem } from '../config/MarketAllocation.types';
interface VaultPendingTimelocksProps {
  chainId: ChainId;
  pendingTimelocks: PendingTimelockItem[];
  currentSupplyCap: BigIntish;
  /** The row's market tokens, which carry the USD price and logo the cap is shown with. */
  loanAsset: InterfaceInputToken;
  collateralAsset: InterfaceInputToken | null;
}
const FUNCTION_NAMES = {
  setCap: 'SetCap',
  removeMarket: 'RemoveMarket'
} as const;
const VaultPendingTimelocks = t0 => {
  const $ = _c(26);
  const {
    chainId,
    pendingTimelocks,
    currentSupplyCap,
    loanAsset,
    collateralAsset
  } = t0;
  const hasCriticalAction = pendingTimelocks.some(item => getTimelockFunctionConfig({
    functionName: FUNCTION_NAMES[item.type],
    hasPreviousValue: isDefined(currentSupplyCap)
  })?.criticality === "high");
  const iconColorClass = hasCriticalAction ? "text-destructive-foreground" : "text-info-foreground";
  let t1;
  if ($[0] !== pendingTimelocks.length) {
    t1 = formatPendingActionsLabel(pendingTimelocks.length);
    $[0] = pendingTimelocks.length;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <span className="text-foreground text-sm">{t1}</span>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== chainId || $[5] !== collateralAsset || $[6] !== currentSupplyCap || $[7] !== loanAsset || $[8] !== pendingTimelocks) {
    let t4;
    if ($[10] !== chainId || $[11] !== collateralAsset || $[12] !== currentSupplyCap || $[13] !== loanAsset) {
      t4 = item_0 => <StyledVaultPendingTimelock.Container key={`${item_0.txHash}-${item_0.type}`} selectable={false}>{item_0.type === "removeMarket" ? <VaultPendingTimelockRemoveMarket chainId={chainId} collateralAsset={collateralAsset} loanAsset={loanAsset} functionName={FUNCTION_NAMES[item_0.type]} marketId={item_0.market.marketId as MarketId} /> : <VaultPendingTimelockChangeCap chainId={chainId} collateralAsset={collateralAsset} loanAsset={loanAsset} supplyCap={item_0.supplyCap} currentSupplyCap={currentSupplyCap} functionName={FUNCTION_NAMES[item_0.type]} marketId={item_0.market.marketId as MarketId} />}<VaultPendingTimelockCountdown validAt={item_0.executableAt} /></StyledVaultPendingTimelock.Container>;
      $[10] = chainId;
      $[11] = collateralAsset;
      $[12] = currentSupplyCap;
      $[13] = loanAsset;
      $[14] = t4;
    } else {
      t4 = $[14];
    }
    t3 = pendingTimelocks.map(t4);
    $[4] = chainId;
    $[5] = collateralAsset;
    $[6] = currentSupplyCap;
    $[7] = loanAsset;
    $[8] = pendingTimelocks;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[15] !== t2 || $[16] !== t3) {
    t4 = <div className="p-sm gap-sm flex flex-col">{t2}{t3}</div>;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  const content = t4;
  let t5;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Timelock />;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== iconColorClass) {
    t6 = cn("size-5", iconColorClass);
    $[19] = iconColorClass;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== t6) {
    t7 = <Icon icon={t5} className={t6} data-testid="market-allocation-pending-timelocks" />;
    $[21] = t6;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== content || $[24] !== t7) {
    t8 = <VaultPendingTimelockInfo content={content}>{t7}</VaultPendingTimelockInfo>;
    $[23] = content;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  return t8;
};
export default VaultPendingTimelocks;